import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Loader2, RotateCw, ExternalLink, Mic, Play, Image as ImageIcon, Video, Sticker } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fmtSize = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "");
const AUTO_MAX = 20 * 1024 * 1024;

export const mediaLabel = (a) => {
  if (a.sticker) return "Sticker";
  if (a.kind === "gif") return "GIF";
  if (a.kind === "photo") return "Foto";
  if (a.kind === "video") return `${a.round ? "Video circolare" : "Video"} ${fmtDur(a.duration)}`.trim();
  if (a.kind === "audio") return `${a.voice ? "Vocale" : "Audio"} ${fmtDur(a.duration)}`.trim();
  return "Documento";
};

// Space reserved before loading (Telegram width/height, else a fixed box): the chat never changes height when media arrive.
export function MediaBox({ a, children }) {
  const w = Number(a.width) || 0, h = Number(a.height) || 0;
  const style = w && h ? { width: Math.round(w * Math.min(1, 260 / w, 288 / h)), aspectRatio: `${w} / ${h}` } : { width: 240, height: 240 };
  return <div className="relative max-w-full overflow-hidden rounded-lg bg-black/30" style={style} data-testid="media-box">{children}</div>;
}

const statusUrl = (url, want, retry) => url.replace(/\/tg-media\/([^?]+)\?/, "/tg-media/$1/status?") + `&want=${want}${retry ? "&retry=1" : ""}`;

// Temporary copy (24 h cache) downloaded from Telegram by the account's server: asks the state until ready.
function useMediaState(a, want) {
  const [st, setSt] = useState(want ? { state: "downloading", progress: 0 } : null);
  const [ver, setVer] = useState(0);
  const retryFlag = useRef(false);
  useEffect(() => {
    if (!want) return undefined;
    let stop = false;
    (async () => {
      for (let i = 0; i < 240 && !stop; i += 1) {
        try {
          const r = await fetch(statusUrl(a.url, want, retryFlag.current), { credentials: "include" });
          retryFlag.current = false;
          const d = r.ok ? await r.json() : { state: "error", error: "Media non disponibile" };
          if (stop) return;
          setSt(d);
          if (d.state !== "downloading") return;
        } catch { /* network blip: ask again */ }
        await new Promise((res) => setTimeout(res, i < 10 ? 1000 : 2500));
      }
      if (!stop) setSt({ state: "error", error: "Download da Telegram troppo lento" });
    })();
    return () => { stop = true; };
  }, [a.url, want, ver]);
  const retry = useCallback(() => { retryFlag.current = true; setSt({ state: "downloading", progress: 0 }); setVer((v) => v + 1); }, []);
  return [st, retry];
}

function Placeholder({ a, thumbUrl, children }) {
  const Icon = a.sticker ? Sticker : a.kind === "video" ? Video : ImageIcon;
  return (
    <MediaBox a={a}>
      {thumbUrl ? <img src={thumbUrl} alt="" className="absolute inset-0 h-full w-full object-cover blur-[1px]" />
        : <div className="absolute inset-0 flex items-center justify-center"><Icon className="h-10 w-10 text-slate-600" /></div>}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-black/60 px-2 py-1 text-[11px] text-slate-200" data-testid="tg-media-label">{children}</div>
    </MediaBox>
  );
}

function ErrorRow({ st, onRetry, tgLink }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-red-300" data-testid="tg-media-error">
      {st?.error || "Media non disponibile"}
      {!["too_large", "ttl"].includes(st?.reason) && <button onClick={onRetry} className="flex items-center gap-1 underline" data-testid="tg-media-retry"><RotateCw className="h-3 w-3" />Riprova</button>}
      {tgLink && <a href={tgLink} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline" data-testid="tg-media-open-telegram"><ExternalLink className="h-3 w-3" />Apri su Telegram</a>}
    </div>
  );
}

const Progress = ({ st, a }) => (
  <><Loader2 className="h-3 w-3 animate-spin" />{mediaLabel(a)} · {st?.progress ? `${Math.round(st.progress * 100)}%` : "caricamento…"} {fmtSize(a.size)}</>
);

// Telegram chat media: shown only, temporary 24 h cache (never Vault, never kept); photos open full screen, videos and voice play in the site.
export function TgMedia({ a, tgLink, blurred }) {
  if (a.ttl) return <MediaBox a={a}><div className="absolute inset-0 flex items-center justify-center p-3 text-center text-[11px] text-amber-200" data-testid="tg-media-ttl">Media a visualizzazione singola, aprilo su Telegram</div></MediaBox>;
  return <TgMediaInner a={a} tgLink={tgLink} blurred={blurred} />;
}

function TgMediaInner({ a, tgLink, blurred }) {
  const visual = ["photo", "gif", "video"].includes(a.kind);
  const autoFull = !(a.kind === "video" && !a.round) && (a.size || 0) <= AUTO_MAX;
  const [opened, setOpened] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [full, retryFull] = useMediaState(a, autoFull || opened ? "full" : null);
  const [thumb] = useMediaState(a, visual && !autoFull && !(full?.state === "ready") ? "thumb" : null);
  const thumbUrl = thumb?.state === "ready" ? `${a.url}&thumb=1` : null;
  const ready = full?.state === "ready";
  const failed = full?.state === "error";
  const tid = `tg-media-${a.kind}`;

  if (a.kind === "photo" || a.kind === "gif") {
    return (
      <div data-testid={tid} className="space-y-1">
        {failed && <ErrorRow st={full} onRetry={retryFull} tgLink={tgLink} />}
        {ready ? (
          <MediaBox a={a}>
            <button type="button" onClick={() => setZoom(true)} className="block h-full w-full" data-testid="tg-media-open-full">
              <img src={a.url} alt={a.filename} className={`h-full w-full object-cover ${blurred ? "blur-md" : ""}`} />
            </button>
            <Dialog open={zoom} onOpenChange={setZoom}>
              <DialogContent className="max-w-[95vw] border-0 bg-black/95 p-2" data-testid="tg-media-fullscreen">
                <DialogTitle className="sr-only">{mediaLabel(a)}</DialogTitle>
                <img src={a.url} alt={a.filename} className="max-h-[88vh] w-full object-contain" />
              </DialogContent>
            </Dialog>
          </MediaBox>
        ) : <Placeholder a={a} thumbUrl={thumbUrl}>{failed ? mediaLabel(a) : <Progress st={full} a={a} />}</Placeholder>}
      </div>
    );
  }
  if (a.kind === "video") {
    return (
      <div data-testid={tid} className="space-y-1">
        {failed && <ErrorRow st={full} onRetry={retryFull} tgLink={tgLink} />}
        {ready ? (
          <MediaBox a={a}><video src={a.url} controls autoPlay={opened} preload="metadata" className={`h-full w-full object-contain bg-black ${blurred ? "blur-md" : ""}`} /></MediaBox>
        ) : (
          <button type="button" onClick={() => setOpened(true)} disabled={opened && !failed} className="block text-left" data-testid="tg-media-video-play">
            <Placeholder a={a} thumbUrl={thumbUrl}>
              {opened && !failed ? <Progress st={full} a={a} /> : <><Play className="h-3.5 w-3.5" />{mediaLabel(a)} {fmtSize(a.size)}</>}
            </Placeholder>
            {opened && !failed && <div className="mt-1 h-1 w-full overflow-hidden rounded bg-white/10"><div className="h-full bg-violet-500 transition-[width]" style={{ width: `${Math.round((full?.progress || 0) * 100)}%` }} data-testid="tg-media-progress" /></div>}
          </button>
        )}
      </div>
    );
  }
  if (a.kind === "audio") {
    return (
      <div data-testid={a.voice ? "tg-media-voice" : tid} className="space-y-1">
        <div className="flex items-center gap-1 text-[10px] text-slate-400">{a.voice ? <Mic className="h-3 w-3" /> : null}{mediaLabel(a)}</div>
        {failed && <ErrorRow st={full} onRetry={retryFull} tgLink={tgLink} />}
        {ready ? <audio src={a.url} controls preload="metadata" className="max-w-[260px]" />
          : !failed && <div className="flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="tg-media-loading"><Progress st={full} a={a} /></div>}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {failed && <ErrorRow st={full} onRetry={retryFull} tgLink={tgLink} />}
      <a href={ready ? a.url : undefined} onClick={() => !ready && setOpened(true)} target="_blank" rel="noreferrer" className="flex cursor-pointer items-center gap-2 rounded-lg bg-black/20 px-2.5 py-2 text-xs hover:bg-black/30" data-testid="tg-media-file">
        {full?.state === "downloading" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <FileText className="h-4 w-4 shrink-0" />}
        <span className="truncate underline">{a.filename}</span><span className="text-white/50 shrink-0">{fmtSize(a.size)}</span>
      </a>
    </div>
  );
}
